import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    inquiry_type: 'general',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Thank you! We will contact you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        inquiry_type: 'general',
        message: ''
      });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 data-testid="contact-title" className="text-5xl md:text-7xl font-black tracking-tighter uppercase mb-6">
            LET'S <span className="text-primary">CONNECT</span>
          </h1>
          <p data-testid="contact-subtitle" className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Get in touch for a free energy assessment and discover how much you could save
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {/* Contact Info Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            data-testid="contact-phone-card"
            className="glass p-8 hover:bg-white/5 transition-all hover:glow-green"
          >
            <Phone className="w-12 h-12 text-primary mb-4" />
            <h3 className="text-xl font-semibold mb-3 uppercase">Phone</h3>
            <a href="tel:+17787292202" className="text-muted-foreground hover:text-primary transition-colors">
              (778) 729-2202
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            data-testid="contact-email-card"
            className="glass p-8 hover:bg-white/5 transition-all hover:glow-green"
          >
            <Mail className="w-12 h-12 text-primary mb-4" />
            <h3 className="text-xl font-semibold mb-3 uppercase">Email</h3>
            <a href="mailto:info@greensmart.ltd" className="text-muted-foreground hover:text-primary transition-colors">
              info@greensmart.ltd
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            data-testid="contact-location-card"
            className="glass p-8 hover:bg-white/5 transition-all hover:glow-green"
          >
            <MapPin className="w-12 h-12 text-primary mb-4" />
            <h3 className="text-xl font-semibold mb-3 uppercase">Location</h3>
            <p className="text-muted-foreground">
              Vancouver, BC, Canada<br />
              Toronto, ON, Canada<br />
              Miami, FLA, US
            </p>
          </motion.div>
        </div>

        {/* Contact Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          data-testid="contact-form-section"
          className="max-w-4xl mx-auto glass p-8 md:p-12"
        >
          <h2 className="text-3xl font-bold tracking-tight mb-8 uppercase text-center">
            SEND US A <span className="text-primary">MESSAGE</span>
          </h2>

          <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-mono uppercase tracking-wider text-muted-foreground mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  data-testid="contact-name-input"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full bg-black/50 border border-white/10 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 font-mono text-sm text-foreground outline-none"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-mono uppercase tracking-wider text-muted-foreground mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  data-testid="contact-email-input"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full bg-black/50 border border-white/10 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 font-mono text-sm text-foreground outline-none"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-mono uppercase tracking-wider text-muted-foreground mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  data-testid="contact-phone-input"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full bg-black/50 border border-white/10 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 font-mono text-sm text-foreground outline-none"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-mono uppercase tracking-wider text-muted-foreground mb-2">
                  Company
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  data-testid="contact-company-input"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full bg-black/50 border border-white/10 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 font-mono text-sm text-foreground outline-none"
                />
              </div>
            </div>

            <div>
              <label htmlFor="inquiry_type" className="block text-sm font-mono uppercase tracking-wider text-muted-foreground mb-2">
                Inquiry Type
              </label>
              <select
                id="inquiry_type"
                name="inquiry_type"
                data-testid="contact-inquiry-type-select"
                value={formData.inquiry_type}
                onChange={handleChange}
                className="w-full bg-black/50 border border-white/10 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 font-mono text-sm text-foreground outline-none"
              >
                <option value="general">General Inquiry</option>
                <option value="quote">Request Quote</option>
                <option value="assessment">Energy Assessment</option>
                <option value="investment">Investment Opportunity</option>
                <option value="partnership">Partnership/Agent</option>
              </select>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-mono uppercase tracking-wider text-muted-foreground mb-2">
                Message *
              </label>
              <textarea
                id="message"
                name="message"
                data-testid="contact-message-textarea"
                required
                rows={6}
                value={formData.message}
                onChange={handleChange}
                className="w-full bg-black/50 border border-white/10 focus:border-primary focus:ring-1 focus:ring-primary p-4 font-mono text-sm text-foreground outline-none resize-none"
              />
            </div>

            <button
              type="submit"
              data-testid="contact-submit-btn"
              disabled={loading}
              className="w-full px-8 py-4 bg-primary text-black font-mono uppercase tracking-wider text-sm font-bold hover:bg-primary/90 transition-all hover:glow-green disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center"
            >
              {loading ? 'SENDING...' : 'SEND MESSAGE'}
              {!loading && <Send className="ml-2" size={18} />}
            </button>
          </form>
        </motion.div>

        {/* Portal Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          <div data-testid="investor-portal-section" className="glass p-8 hover:bg-white/5 transition-all">
            <h3 className="text-2xl font-bold mb-4 uppercase">INVEST IN <span className="text-primary">GREENSMART</span></h3>
            <p className="text-muted-foreground mb-6">
              Join us in building a sustainable future. Access your investor portal for updates and opportunities.
            </p>
            <a
              href="/portal"
              data-testid="investor-portal-btn"
              className="inline-block px-8 py-3 border border-primary/50 text-primary font-mono uppercase tracking-wider text-sm hover:bg-primary/10 transition-colors"
            >
              INVESTOR PORTAL
            </a>
          </div>

          <div data-testid="agent-portal-section" className="glass p-8 hover:bg-white/5 transition-all">
            <h3 className="text-2xl font-bold mb-4 uppercase">AGENT <span className="text-primary">PORTAL</span></h3>
            <p className="text-muted-foreground mb-6">
              Partners and resellers can access exclusive resources, training materials, and sales support.
            </p>
            <a
              href="/portal"
              data-testid="agent-portal-btn"
              className="inline-block px-8 py-3 border border-primary/50 text-primary font-mono uppercase tracking-wider text-sm hover:bg-primary/10 transition-colors"
            >
              AGENT PORTAL
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
